import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  Save, Building2, Receipt, Loader2, CheckCircle2, AlertCircle, Info, ShieldAlert,
  FileText,
} from 'lucide-react';
import PhoneInput from '../../components/ui/PhoneInput';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin`;

// Client-side format checks — must mirror backend regex in gst_invoice.py
const GSTIN_RX = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[0-9A-Z]{1}[Z]{1}[0-9A-Z]{1}$/;
const PAN_RX   = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;

const REQUIRED_FIELDS = [
  ['company_legal_name', 'Company Legal Name'],
  ['company_gstin',      'Company GSTIN'],
  ['company_pan',        'Company PAN'],
  ['company_address',    'Registered Address'],
  ['company_state_code', 'State'],
];

const GstSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState(null);
  const [states, setStates] = useState([]);
  const [toast, setToast] = useState(null);
  const [readiness, setReadiness] = useState({ ready: false, missing: [], invalid: [] });
  const [touched, setTouched] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const [s, st] = await Promise.all([
          axios.get(`${API}/settings/gst`, { withCredentials: true }),
          axios.get(`${API}/settings/states`, { withCredentials: true }),
        ]);
        setSettings(s.data.settings);
        setReadiness(s.data.readiness || { ready: false, missing: [], invalid: [] });
        setStates(st.data.states || []);
      } finally { setLoading(false); }
    })();
  }, []);

  const upd = (k, v) => setSettings((p) => ({ ...p, [k]: v }));
  const markTouched = (k) => setTouched((p) => ({ ...p, [k]: true }));

  // Inline client-side validation — shown once user interacts with the field
  const fieldError = (k) => {
    if (!settings) return null;
    const v = (settings[k] || '').toString().trim();
    const requiredMap = Object.fromEntries(REQUIRED_FIELDS);
    if (requiredMap[k] && !v) return `${requiredMap[k]} is required.`;
    if (k === 'company_gstin' && v && !GSTIN_RX.test(v.toUpperCase())) {
      return 'GSTIN must be 15 chars (e.g. 27AAAAA0000A1Z5).';
    }
    if (k === 'company_pan' && v && !PAN_RX.test(v.toUpperCase())) {
      return 'PAN must be 10 chars (e.g. AAAAA1234A).';
    }
    if (k === 'company_gstin' || k === 'company_state_code') {
      const g = (settings.company_gstin || '').trim().toUpperCase();
      const sc = (settings.company_state_code || '').trim();
      if (g && sc && GSTIN_RX.test(g) && g.slice(0, 2) !== sc) {
        return 'GSTIN state prefix does not match selected state.';
      }
    }
    return null;
  };

  const formErrors = useMemo(() => {
    if (!settings) return [];
    return REQUIRED_FIELDS
      .map(([k, label]) => ({ k, label, err: fieldError(k) }))
      .filter((x) => x.err);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings]);

  const hasClientErrors = formErrors.length > 0 ||
    fieldError('company_gstin') || fieldError('company_pan');

  const save = async () => {
    // Force-show all field errors on submit
    setTouched(Object.fromEntries(REQUIRED_FIELDS.map(([k]) => [k, true])));
    if (hasClientErrors) {
      setToast({ type: 'err', msg: 'Please fix the highlighted fields before saving.' });
      setTimeout(() => setToast(null), 4000);
      return;
    }
    setSaving(true);
    try {
      const payload = {
        company_legal_name: settings.company_legal_name,
        company_gstin:      settings.company_gstin,
        company_state_code: settings.company_state_code,
        company_state_name: settings.company_state_name,
        company_address:    settings.company_address,
        company_pan:        settings.company_pan,
        company_email:      settings.company_email,
        company_phone:      settings.company_phone,
        invoice_prefix:     settings.invoice_prefix,
        invoice_fy_reset:   settings.invoice_fy_reset,
        hsn_sac_code:       settings.hsn_sac_code,
        gst_rate_pct:       parseFloat(settings.gst_rate_pct) || 18.0,
        tcs_rate_pct:       parseFloat(settings.tcs_rate_pct) || 5.0,
        tcs_threshold_inr:  parseInt(settings.tcs_threshold_inr) || 700000,
        tcs_enabled_for_international: !!settings.tcs_enabled_for_international,
        commission_gst_absorbed_by_acf: !!settings.commission_gst_absorbed_by_acf,
        commission_tds_enabled: !!settings.commission_tds_enabled,
        supplier_tds_rate_default: parseFloat(settings.supplier_tds_rate_default) || 2,
        agent_tds_rate_default: parseFloat(settings.agent_tds_rate_default) || 2,
        agent_commission_base: settings.agent_commission_base || 'pct_of_supplier_commission_gross',
        agent_commission_default_pct: parseFloat(settings.agent_commission_default_pct) || 0,
        auto_credit_agent_commission_wallet: !!settings.auto_credit_agent_commission_wallet,
        gst_invoice_taxable_mode: settings.gst_invoice_taxable_mode || 'markup_only',
        bank_name: settings.bank_name,
        bank_branch: settings.bank_branch,
        bank_account_holder: settings.bank_account_holder,
        bank_account_number: settings.bank_account_number,
        bank_ifsc_code: settings.bank_ifsc_code,
        bank_upi_id: settings.bank_upi_id,
      };
      const r = await axios.put(`${API}/settings/gst`, payload, { withCredentials: true });
      setSettings(r.data.settings);
      setReadiness(r.data.readiness || { ready: false, missing: [], invalid: [] });
      setToast({
        type: 'ok',
        msg: r.data.readiness?.ready
          ? 'GST settings saved. Invoicing is LIVE.'
          : 'GST settings saved. Some required fields are still missing — invoicing remains disabled.',
      });
    } catch (e) {
      setToast({ type: 'err', msg: e?.response?.data?.detail || 'Save failed.' });
    } finally {
      setSaving(false);
      setTimeout(() => setToast(null), 5000);
    }
  };

  const onStateCodeChange = (code) => {
    const match = states.find((s) => s.code === code);
    setSettings((p) => ({ ...p, company_state_code: code, company_state_name: match?.name || '' }));
  };

  if (loading || !settings) {
    return <div className="flex items-center justify-center p-20 text-gray-500">
      <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading…
    </div>;
  }

  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto" data-testid="gst-settings-page">
      <header className="mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">GST & Invoicing Settings</h1>
          <p className="text-sm text-gray-600 mt-1">
            Configure company GSTIN, invoice numbering, and tax rates used for Agent B2B Tax Invoices.
            Bookings by agents WITH a valid GSTIN generate Tax Invoices; B2C and non-GSTIN agents are treated as walk-in (no invoice).
          </p>
        </div>
        <a
          href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/settings/gst/ca-report`}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white rounded-lg text-sm font-semibold whitespace-nowrap self-start"
          data-testid="gst-ca-report-download"
          title="Download the GST & TCS compliance brief for your Chartered Accountant"
        >
          <FileText className="w-4 h-4" /> CA Compliance Report (PDF)
        </a>
      </header>

      {/* Readiness banner — single source of truth for "invoicing live?" */}
      {readiness.ready ? (
        <div
          data-testid="gst-readiness-ok"
          className="mb-5 rounded-lg border border-emerald-200 bg-emerald-50 p-4 flex items-start gap-3"
        >
          <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
          <div className="text-sm">
            <p className="font-semibold text-emerald-900">GST configuration complete — invoicing is LIVE.</p>
            <p className="text-emerald-800 mt-0.5">
              Tax Invoices, Credit Notes, and Supplementary Invoices will be issued against your
              company identity: <span className="font-mono">{settings.company_gstin}</span>.
            </p>
          </div>
        </div>
      ) : (
        <div
          data-testid="gst-readiness-warning"
          className="mb-5 rounded-lg border border-red-200 bg-red-50 p-4 flex items-start gap-3"
        >
          <ShieldAlert className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
          <div className="text-sm flex-1">
            <p className="font-semibold text-red-900">GST configuration incomplete — invoicing is DISABLED.</p>
            <p className="text-red-800 mt-0.5">
              No invoice (Tax / Credit Note / Supplementary) will be generated until the fields below
              are filled in correctly. Bookings will still complete normally.
            </p>
            {(readiness.missing?.length > 0 || readiness.invalid?.length > 0) && (
              <ul className="list-disc list-inside text-red-800 mt-2 space-y-0.5">
                {readiness.missing?.map((m) => (
                  <li key={`m-${m}`}>Missing: <strong>{m}</strong></li>
                ))}
                {readiness.invalid?.map((m) => (
                  <li key={`i-${m}`}>Invalid: <strong>{m}</strong></li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      {toast && (
        <div className={`mb-4 px-4 py-2 rounded-lg text-sm flex items-center gap-2 ${
          toast.type === 'ok' ? 'bg-emerald-50 text-emerald-800' : 'bg-red-50 text-red-800'
        }`} data-testid="gst-settings-toast">
          {toast.type === 'ok' ? <CheckCircle2 className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
          {toast.msg}
        </div>
      )}

      <Section title="Company Info" icon={<Building2 className="w-4 h-4" />}>
        <Grid>
          <Field label="Legal Name" required error={touched.company_legal_name && fieldError('company_legal_name')}>
            <input type="text" value={settings.company_legal_name || ''}
              onChange={(e) => upd('company_legal_name', e.target.value)}
              onBlur={() => markTouched('company_legal_name')}
              className={inputCls(touched.company_legal_name && fieldError('company_legal_name'))}
              data-testid="gst-company-name" />
          </Field>
          <Field label="Company GSTIN (15 chars)" required error={touched.company_gstin && fieldError('company_gstin')}>
            <input type="text" value={settings.company_gstin || ''} maxLength={15}
              placeholder="27AAAAA0000A1Z5"
              onChange={(e) => upd('company_gstin', e.target.value.toUpperCase())}
              onBlur={() => markTouched('company_gstin')}
              className={inputCls(touched.company_gstin && fieldError('company_gstin')) + " font-mono"}
              data-testid="gst-company-gstin" />
          </Field>
          <Field label="State" required error={touched.company_state_code && fieldError('company_state_code')}>
            <select value={settings.company_state_code || ''}
              onChange={(e) => { onStateCodeChange(e.target.value); markTouched('company_state_code'); }}
              onBlur={() => markTouched('company_state_code')}
              className={inputCls(touched.company_state_code && fieldError('company_state_code'))}
              data-testid="gst-company-state">
              <option value="">-- Select State --</option>
              {states.map((s) => <option key={s.code} value={s.code}>{s.code} · {s.name}</option>)}
            </select>
          </Field>
          <Field label="Company PAN (10 chars)" required error={touched.company_pan && fieldError('company_pan')}>
            <input type="text" maxLength={10} value={settings.company_pan || ''}
              placeholder="AAAAA1234A"
              onChange={(e) => upd('company_pan', e.target.value.toUpperCase())}
              onBlur={() => markTouched('company_pan')}
              className={inputCls(touched.company_pan && fieldError('company_pan')) + " font-mono"}
              data-testid="gst-company-pan" />
          </Field>
          <Field label="Registered Address" span={2} required error={touched.company_address && fieldError('company_address')}>
            <textarea rows={2} value={settings.company_address || ''}
              onChange={(e) => upd('company_address', e.target.value)}
              onBlur={() => markTouched('company_address')}
              className={inputCls(touched.company_address && fieldError('company_address'))}
              data-testid="gst-company-address" />
          </Field>
          <Field label="Accounts Email">
            <input type="email" value={settings.company_email || ''}
              onChange={(e) => upd('company_email', e.target.value)}
              className={inputCls(null)} data-testid="gst-company-email" />
          </Field>
          <Field label="Accounts Phone">
            <PhoneInput
              value={settings.company_phone || ''}
              onChange={(v) => upd('company_phone', v)}
              defaultCountry="IN"
              testid="gst-company-phone"
            />
          </Field>
        </Grid>
      </Section>

      <Section title="Invoice Numbering" icon={<Receipt className="w-4 h-4" />}>        <Grid>
          <Field label="Invoice Prefix (e.g. ACF/)">
            <input type="text" value={settings.invoice_prefix || 'ACF/'}
              onChange={(e) => upd('invoice_prefix', e.target.value)}
              className={inputCls(null)} data-testid="gst-invoice-prefix" />
          </Field>
          <Field label="Format Preview">
            <div className="px-3 py-2 bg-gray-50 rounded border border-gray-200 font-mono text-sm">
              {settings.invoice_prefix || 'ACF/'}26-27/00001
            </div>
          </Field>
          <Field label="HSN/SAC Code">
            <input type="text" value={settings.hsn_sac_code || '998551'}
              onChange={(e) => upd('hsn_sac_code', e.target.value)}
              className={inputCls(null) + " font-mono"} />
          </Field>
          <div className="flex items-center gap-2 mt-5">
            <input id="fy_reset" type="checkbox"
              checked={!!settings.invoice_fy_reset}
              onChange={(e) => upd('invoice_fy_reset', e.target.checked)} />
            <label htmlFor="fy_reset" className="text-sm text-gray-700">Reset counter every fiscal year (recommended)</label>
          </div>
        </Grid>
        <div className="mt-3 flex gap-2 items-start bg-blue-50 text-blue-800 text-xs rounded p-2">
          <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
          Invoice counter is atomic and FY-partitioned. Past counter values are immutable — the number assigned to a raised invoice never changes.
        </div>
      </Section>

      <Section title="Tax Rates" icon={<Receipt className="w-4 h-4" />}>
        <Grid>
          <Field label="GST Rate %">
            <input type="number" step="0.1" value={settings.gst_rate_pct || 18}
              onChange={(e) => upd('gst_rate_pct', e.target.value)}
              className={inputCls(null)} />
          </Field>
          <Field label="TCS Rate % (international)">
            <input type="number" step="0.1" value={settings.tcs_rate_pct || 5}
              onChange={(e) => upd('tcs_rate_pct', e.target.value)}
              disabled={!settings.tcs_enabled_for_international}
              className={inputCls(null) + (!settings.tcs_enabled_for_international ? " opacity-50 cursor-not-allowed" : "")} />
          </Field>
          <Field label="TCS Threshold ₹ per PAN/FY">
            <input type="number" value={settings.tcs_threshold_inr || 700000}
              onChange={(e) => upd('tcs_threshold_inr', e.target.value)}
              disabled={!settings.tcs_enabled_for_international}
              className={inputCls(null) + (!settings.tcs_enabled_for_international ? " opacity-50 cursor-not-allowed" : "")} />
          </Field>
          <div className="flex items-center gap-2 mt-5">
            <input id="tcs_intl" type="checkbox"
              checked={!!settings.tcs_enabled_for_international}
              onChange={(e) => upd('tcs_enabled_for_international', e.target.checked)}
              data-testid="gst-tcs-intl" />
            <label htmlFor="tcs_intl" className="text-sm text-gray-700">
              Collect TCS on international bookings (Sec 206C(1G))
            </label>
          </div>
        </Grid>

        {!settings.tcs_enabled_for_international ? (
          <div className="mt-3 flex gap-2 items-start bg-red-50 text-red-900 text-xs rounded p-2"
               data-testid="tcs-disabled-note">
            <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <div>
              <strong>TCS collection is OFF.</strong> No TCS will be added to any invoice. Per
              CA direction dated 2026-04-23 — TCS under Sec 206C(1G) is not being collected from
              B2B agent bookings. Turn back on only after re-confirming with your CA.
            </div>
          </div>
        ) : (
          <div className="mt-3 flex gap-2 items-start bg-amber-50 text-amber-900 text-xs rounded p-2">
            <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
            TCS is currently applied flat on every international invoice. Per-PAN FY threshold enforcement (₹7L/FY) is not yet live.
          </div>
        )}
      </Section>

      {/* Commission & TDS (194H) — feature-flagged */}
      <Section title="Commission & TDS (194H)" icon={<Receipt className="w-4 h-4" />}>
        <div className="flex items-start gap-3 p-3 rounded-lg border border-blue-200 bg-blue-50 mb-4">
          <input
            id="comm_tds_en"
            type="checkbox"
            checked={!!settings.commission_tds_enabled}
            onChange={(e) => upd('commission_tds_enabled', e.target.checked)}
            data-testid="commission-tds-enabled"
            className="mt-1"
          />
          <label htmlFor="comm_tds_en" className="text-sm text-gray-800 cursor-pointer flex-1">
            <span className="font-semibold">Enable commission & TDS accounting (beta).</span>
            <p className="text-xs text-gray-600 mt-1">
              When OFF, booking and invoicing behave exactly as today. When ON, stamps
              supplier/agent commission and TDS fields per booking (no change to ticket prices).
            </p>
          </label>
        </div>
        <Grid>
          <Field label="Supplier TDS % (194H)">
            <input
              type="number"
              step="0.01"
              value={settings.supplier_tds_rate_default ?? 2}
              onChange={(e) => upd('supplier_tds_rate_default', parseFloat(e.target.value) || 0)}
              className={inputCls(null)}
              data-testid="supplier-tds-rate"
            />
          </Field>
          <Field label="Agent TDS % (194H)">
            <input
              type="number"
              step="0.01"
              value={settings.agent_tds_rate_default ?? 2}
              onChange={(e) => upd('agent_tds_rate_default', parseFloat(e.target.value) || 0)}
              className={inputCls(null)}
              data-testid="agent-tds-rate"
            />
          </Field>
          <Field label="Agent commission base">
            <select
              value={settings.agent_commission_base || 'pct_of_supplier_commission_gross'}
              onChange={(e) => upd('agent_commission_base', e.target.value)}
              className={inputCls(null)}
              data-testid="agent-commission-base"
            >
              <option value="pct_of_supplier_commission_gross">% of supplier commission (gross)</option>
              <option value="fixed_per_booking">Fixed per booking</option>
              <option value="pct_of_acf_net_profit">% of ACF net profit</option>
              <option value="pct_of_supplier_net">% of supplier net (after TDS) — discouraged</option>
            </select>
          </Field>
          <Field label="Default agent share %">
            <input
              type="number"
              step="0.1"
              value={settings.agent_commission_default_pct ?? 0}
              onChange={(e) => upd('agent_commission_default_pct', parseFloat(e.target.value) || 0)}
              className={inputCls(null)}
              data-testid="agent-commission-default-pct"
            />
          </Field>
          <Field label="GST invoice taxable mode" span={2}>
            <select
              value={settings.gst_invoice_taxable_mode || 'markup_only'}
              onChange={(e) => upd('gst_invoice_taxable_mode', e.target.value)}
              className={inputCls(null)}
              data-testid="gst-taxable-mode"
            >
              <option value="markup_only">Markup only (current production)</option>
              <option value="markup_plus_acf_retained_commission">Markup + ACF retained commission</option>
            </select>
          </Field>
        </Grid>
        <p className="mt-3 text-xs text-gray-600">
          <Link to="/admin/compliance/commission-tds" className="font-semibold text-[#1B375B] underline">
            Open Commission &amp; TDS reports
          </Link>
          {' '}(GSTR-7, Form 16A, 26AS matching)
        </p>
        <div className="mt-3">
          <button
            type="button"
            className="text-xs font-semibold text-[#1B375B] underline"
            onClick={async () => {
              try {
                const r = await axios.post(`${API}/commission/backfill?days=30&limit=500`, null, { withCredentials: true });
                setToast({ type: 'ok', msg: `Backfill complete: ${r.data.stamped} bookings stamped` });
              } catch (e) {
                setToast({ type: 'err', msg: e?.response?.data?.detail || 'Backfill failed' });
              }
            }}
          >
            Backfill commission on last 30 days (after enabling module)
          </button>
        </div>
        <div className="mt-3 flex items-center gap-2">
          <input
            id="auto_comm_wallet"
            type="checkbox"
            checked={!!settings.auto_credit_agent_commission_wallet}
            onChange={(e) => upd('auto_credit_agent_commission_wallet', e.target.checked)}
            data-testid="auto-credit-agent-commission"
          />
          <label htmlFor="auto_comm_wallet" className="text-xs text-gray-700">
            Auto-credit agent commission net to wallet when admin posts payable (Phase 2)
          </label>
        </div>
      </Section>

      {/* Commission GST treatment — CA-directed */}
      <Section title="Commission GST Treatment" icon={<Receipt className="w-4 h-4" />}>
        <div className="flex items-start gap-3 p-3 rounded-lg border border-gray-200 bg-gray-50">
          <input id="comm_acf" type="checkbox"
            checked={!!settings.commission_gst_absorbed_by_acf}
            onChange={(e) => upd('commission_gst_absorbed_by_acf', e.target.checked)}
            data-testid="gst-comm-absorbed"
            className="mt-1" />
          <label htmlFor="comm_acf" className="text-sm text-gray-800 cursor-pointer flex-1">
            <span className="font-semibold">ACF absorbs 18% GST on airline commission (CA-directed).</span>
            <p className="text-xs text-gray-600 mt-1">
              When ON: agent invoices remain unchanged (only markup is taxed).
              ACF internally declares commission as taxable turnover and pays the GST
              out of its own commission income. Admin Dashboard shows the MTD liability.
            </p>
            <p className="text-xs text-gray-600 mt-1">
              When OFF: airline commission is added to the invoice's taxable value — agent pays the
              extra GST. Switch back only after re-confirming with your CA.
            </p>
          </label>
        </div>
      </Section>

      <Section title="Bank Details (on Invoice)" icon={<Building2 className="w-4 h-4" />}>
        <p className="text-xs text-gray-500 mb-3">
          These details appear on every generated tax invoice, credit note and supplementary invoice
          so agents can settle payments via NEFT / RTGS / IMPS / UPI. Leave blank to hide the block.
        </p>
        <Grid>
          <Field label="Bank Name">
            <input type="text" value={settings.bank_name || ''}
              onChange={(e) => upd('bank_name', e.target.value)}
              className={inputCls(null)} data-testid="gst-bank-name" />
          </Field>
          <Field label="Branch">
            <input type="text" value={settings.bank_branch || ''}
              onChange={(e) => upd('bank_branch', e.target.value)}
              className={inputCls(null)} data-testid="gst-bank-branch" />
          </Field>
          <Field label="Account Holder Name" span={2}>
            <input type="text" value={settings.bank_account_holder || ''}
              onChange={(e) => upd('bank_account_holder', e.target.value)}
              placeholder={settings.company_legal_name || 'ACF Tourism Pvt Ltd'}
              className={inputCls(null)} data-testid="gst-bank-holder" />
          </Field>
          <Field label="Account Number">
            <input type="text" value={settings.bank_account_number || ''}
              onChange={(e) => upd('bank_account_number', e.target.value)}
              className={inputCls(null) + " font-mono"} data-testid="gst-bank-account" />
          </Field>
          <Field label="IFSC Code">
            <input type="text" value={settings.bank_ifsc_code || ''}
              onChange={(e) => upd('bank_ifsc_code', (e.target.value || '').toUpperCase())}
              maxLength={11}
              placeholder="e.g. HDFC0001234"
              className={inputCls(null) + " font-mono"} data-testid="gst-bank-ifsc" />
          </Field>
          <Field label="UPI ID (optional)" span={2}>
            <input type="text" value={settings.bank_upi_id || ''}
              onChange={(e) => upd('bank_upi_id', e.target.value)}
              placeholder="e.g. acftourism@hdfcbank"
              className={inputCls(null) + " font-mono"} data-testid="gst-bank-upi" />
          </Field>
        </Grid>
      </Section>

      <div className="flex justify-end gap-2 mt-6">
        <button
          onClick={save}
          disabled={saving}
          className="flex items-center gap-2 px-6 py-2 bg-[#F5A623] text-[#0C1D36] font-semibold rounded-lg hover:bg-[#F5A623]/90 disabled:opacity-60"
          data-testid="gst-settings-save"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save Settings
        </button>
      </div>
    </div>
  );
};

const inputCls = (err) =>
  `w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 ${
    err
      ? 'border-red-300 focus:ring-red-300 bg-red-50/40'
      : 'border-gray-300 focus:ring-[#F5A623]'
  }`;

const Section = ({ title, icon, children }) => (
  <section className="bg-white border border-gray-200 rounded-lg p-4 md:p-5 mb-4">
    <h2 className="flex items-center gap-2 text-base font-semibold text-gray-900 mb-4">
      {icon} {title}
    </h2>
    {children}
  </section>
);

const Grid = ({ children }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{children}</div>
);

const Field = ({ label, children, span, required, error }) => (
  <div className={span === 2 ? "md:col-span-2" : ""}>
    <label className="block text-xs font-semibold text-gray-700 mb-1 uppercase tracking-wide">
      {label} {required && <span className="text-red-500 normal-case">*</span>}
    </label>
    {children}
    {error && (
      <p className="text-xs text-red-600 mt-1 flex items-center gap-1" data-testid="gst-field-error">
        <AlertCircle className="w-3 h-3" /> {error}
      </p>
    )}
  </div>
);

export default GstSettings;
